import { Injectable } from '@angular/core';
import { ViewContainerRef, TemplateRef } from '@angular/core';
import { Subject } from 'rxjs/Subject';
import { Observable } from 'rxjs/Observable';
import { Subscription } from 'rxjs/Subscription';

import { TdLoadingContext } from '../directives/loading.directive';
import { TdLoadingComponent, LoadingMode, LoadingStrategy, LoadingType } from '../loading.component';
import { TdLoadingFactory, ILoadingRef } from './loading.factory';

export interface ITdLoadingConfig {
  name: string;
  type?: LoadingType;
  mode?: LoadingMode;
  color?: 'primary' | 'accent' | 'warn';
}
export class TdLoadingConfig implements ITdLoadingConfig {
  name: string;
  type?: LoadingType;
  mode?: LoadingMode;
  color?: 'primary' | 'accent' | 'warn';
/**
 * @param {?} config
 */
constructor(config: ITdLoadingConfig) {
    this.name = config.name;
    if (!this.name) {
      throw Error('Name is required for [TdLoading] configuration.');
    }
    this.mode = config.mode ? config.mode : LoadingMode.Indeterminate;
    this.type = config.type ? config.type : LoadingType.Circular;
    this.color = config.color ? config.color : 'primary';
  }
}

function TdLoadingConfig_tsickle_Closure_declarations() {
/** @type {?} */
TdLoadingConfig.prototype.name;
/** @type {?} */
TdLoadingConfig.prototype.type;
/** @type {?} */
TdLoadingConfig.prototype.mode;
/** @type {?} */
TdLoadingConfig.prototype.color;
}


export interface ITdLoadingDirectiveConfig extends ITdLoadingConfig {
  strategy?: LoadingStrategy;
}
export class TdLoadingDirectiveConfig extends TdLoadingConfig implements ITdLoadingDirectiveConfig {
  name: string;
  type: LoadingType;
  mode: LoadingMode;
  strategy: LoadingStrategy;
/**
 * @param {?} config
 */
constructor(config: ITdLoadingDirectiveConfig) {
    super(config);
    this.strategy = config.strategy ? config.strategy : LoadingStrategy.Replace;
  }
}

function TdLoadingDirectiveConfig_tsickle_Closure_declarations() {
/** @type {?} */
TdLoadingDirectiveConfig.prototype.name;
/** @type {?} */
TdLoadingDirectiveConfig.prototype.type;
/** @type {?} */
TdLoadingDirectiveConfig.prototype.mode;
/** @type {?} */
TdLoadingDirectiveConfig.prototype.strategy;
}

export class TdLoadingService {
private _context: {[key: string]: ILoadingRef} = {};
private _timeouts: {[key: string]: any} = {};
/**
 * @param {?} _loadingFactory
 */
constructor(private _loadingFactory: TdLoadingFactory) {
    this.create({
      name: 'td-loading-main',
    });
  }
/**
 * params:
 * - config: ILoadingDirectiveConfig
 * - viewContainerRef: ViewContainerRef
 * - templateRef: TemplateRef<Object>
 * 
 * Creates an replace loading mask and attaches it to the viewContainerRef.
 * Replaces the templateRef with the mask when a request is registered on it.
 * 
 * NOTE: \@internal usage only.
 * @param {?} config
 * @param {?} viewContainerRef
 * @param {?} templateRef
 * @param {?} context
 * @return {?}
 */
createComponent(config: ITdLoadingDirectiveConfig, viewContainerRef: ViewContainerRef,
                  templateRef: TemplateRef<Object>, context: TdLoadingContext): ILoadingRef {
    let /** @type {?} */ directiveConfig: TdLoadingDirectiveConfig = new TdLoadingDirectiveConfig(config);
    if (this._context[directiveConfig.name]) {
      throw Error(`Name duplication: [TdLoading] directive has a name conflict with ${directiveConfig.name}.`);
    }
    if (directiveConfig.strategy === LoadingStrategy.Overlay) {
      this._context[directiveConfig.name] = this._loadingFactory.createOverlayComponent(directiveConfig, viewContainerRef, templateRef);
    } else {
      this._context[directiveConfig.name] = this._loadingFactory.createReplaceComponent(directiveConfig, viewContainerRef, templateRef, context);
    }
    return this._context[directiveConfig.name];
  }
/**
 * params:
 * - config: ITdLoadingConfig
 * 
 * Creates a fullscreen loading mask and attaches it to the DOM with the given configuration.
 * Only displayed when the mask has a request registered on it.
 * @param {?} config
 * @return {?}
 */
public create(config: ITdLoadingConfig): void {
    let /** @type {?} */ fullscreenConfig: TdLoadingConfig = new TdLoadingConfig(config);
    this.removeComponent(fullscreenConfig.name);
    this._context[fullscreenConfig.name] = this._loadingFactory.createFullScreenComponent(fullscreenConfig);
  }
/**
 * params:
 * - name: string
 * 
 * Removes `loading` component from service context.
 * @param {?} name
 * @return {?}
 */
public removeComponent(name: string): void {
    if (this._context[name]) {
      this._context[name].subject.unsubscribe();
      if (this._context[name].componentRef) {
        this._context[name].componentRef.destroy();
      }
      this._context[name] = undefined;
      delete this._context[name];
    }
  }
/**
 * params:
 * - name: string
 * - registers?: number
 * returns: true if successful
 * 
 * Resolves a request for the loading mask referenced by the name parameter.
 * Can optionally pass registers argument to set a number of register calls.
 * 
 * If no paramemeters are used, then default main mask will be used.
 * 
 * e.g. loadingService.register()
 * @param {?=} name
 * @param {?=} registers
 * @return {?}
 */
public register(name: string = 'td-loading-main', registers: number = 1): boolean {
    // try registering into the service if the loading component has been instanciated or if it exists.
    if (this._context[name]) {
      registers = registers < 1 ? 1 : registers;
      this._context[name].times += registers;
      this._context[name].subject.next(this._context[name].times);
      return true;
    } else {
      // if it doesnt exist, set a timeout so its registered after change detection happens
      // this in case "register" occured on the `ngOnInit` lifehook cycle.
      if (!this._timeouts[name]) {
        this._timeouts[name] = setTimeout(() => {
          this.register(name, registers);
        });
      } else {
        // if it timeout occured and still doesnt exist, it means the tiemout wasnt needed so we clear it.
        this._clearTimeout(name);
      }
    }
    return false;
  }
/**
 * params:
 * - name: string
 * - resolves?: number
 * returns: true if successful
 * 
 * Resolves a request for the loading mask referenced by the name parameter.
 * Can optionally pass resolves argument to set a number of resolve calls.
 * 
 * If no paramemeters are used, then default main mask will be used.
 * 
 * e.g. loadingService.resolve()
 * @param {?=} name
 * @param {?=} resolves
 * @return {?}
 */
public resolve(name: string = 'td-loading-main', resolves: number = 1): boolean {
    // clear timeout if the loading component is "resolved" before its "registered"
    this._clearTimeout(name);
    if (this._context[name]) {
      resolves = resolves < 1 ? 1 : resolves;
      if (this._context[name].times > 0) {
        let /** @type {?} */ times: number = this._context[name].times;
        times -= resolves;
        this._context[name].times = times < 0 ? 0 : times;
      }
      this._context[name].subject.next(this._context[name].times);
      return true;
    }
    return false;
  }
/**
 * params:
 * - name: string
 * returns: true if successful
 * 
 * Resolves all request for the loading mask referenced by the name parameter.
 * 
 * If no paramemeters are used, then default main mask will be used.
 * 
 * e.g. loadingService.resolveAll()
 * @param {?=} name
 * @return {?}
 */
public resolveAll(name: string = 'td-loading-main'): boolean {
    // clear timeout if the loading component is "resolved" before its "registered"
    this._clearTimeout(name);
    if (this._context[name]) {
      this._context[name].times = 0;
      this._context[name].subject.next(this._context[name].times);
      return true;
    }
    return false;
  }
/**
 * params:
 * - name: string
 * - value: number
 * returns: true if successful
 * 
 * Set value on a loading mask referenced by the name parameter.
 * Usage only available if its mode is 'determinate' and if loading is showing.
 * @param {?} name
 * @param {?} value
 * @return {?}
 */
public setValue(name: string, value: number): boolean {
    if (this._context[name]) {
      let /** @type {?} */ instance: TdLoadingComponent = this._context[name].componentRef.instance;
      if (instance.mode === LoadingMode.Determinate && instance.animation) {
        instance.value = value;
        return true;
      }
    }
    return false;
  }
/**
 * Clears timeout linked to the name.
 * @param {?} name Name of the loading component to be cleared
 * @return {?}
 */
private _clearTimeout(name: string): void {
    clearTimeout(this._timeouts[name]);
    delete this._timeouts[name];
  }
static decorators: DecoratorInvocation[] = [
{ type: Injectable },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: TdLoadingFactory, },
];
}

function TdLoadingService_tsickle_Closure_declarations() {
/** @type {?} */
TdLoadingService.decorators;
/**
 * @nocollapse
 * @type {?}
 */
TdLoadingService.ctorParameters;
/** @type {?} */
TdLoadingService.prototype._context;
/** @type {?} */
TdLoadingService.prototype._timeouts;
/** @type {?} */
TdLoadingService.prototype._loadingFactory;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
